import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ACCOUNT_RESET_PHRASE, type AccountResetResult } from '@kontor/shared';
import { api, ApiError } from '../api';
import { useSession } from '../session';

/**
 * Einrichtung → Daten löschen: Das Unternehmen setzt sein Konto selbst auf den Stand ohne Daten zurück. Die Löschung geschieht in der Datenbank und lässt sich nicht
 * rückgängig machen. Zur Bestätigung gehören das Passwort und der abgetippte Text, damit niemand mit einer offenen Sitzung versehentlich alles löscht.
 */
export function DataDeletionPage() {
  const client = useQueryClient();
  const { session } = useSession();
  const [password, setPassword] = useState('');
  const [phrase, setPhrase] = useState('');
  const [result, setResult] = useState<AccountResetResult | null>(null);
  const isOwner = session?.user.role.key === 'owner';

  const reset = useMutation({
    mutationFn: () => api<{ data: AccountResetResult }>('POST', '/account/reset', { password, confirmation: phrase }),
    onSuccess: async (res) => {
      setResult(res.data);
      setPassword('');
      setPhrase('');
      // Alles, was die Oberfläche von den gelöschten Daten noch im Speicher hat, verwerfen. Die Sitzung wird neu geladen, weil Branche und Menü zurückgesetzt sind.
      client.removeQueries({ predicate: (query) => query.queryKey[0] !== 'session' });
      await client.invalidateQueries({ queryKey: ['session'] });
    },
  });

  const error = reset.error instanceof ApiError ? reset.error : null;
  const passwordError = error?.status === 422 ? error.fields.password : undefined;
  const ready = phrase.trim() === ACCOUNT_RESET_PHRASE && password.length > 0 && !reset.isPending;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (ready) reset.mutate();
  };

  return (
    <>
      <section className="card">
        <h2>Daten löschen</h2>
        <p className="muted small">
          Hier setzen Sie Ihr Wirkia-Konto auf den Stand zurück, mit dem es begonnen hat, also ohne Daten. Alles, was Sie angelegt haben, wird aus der Datenbank gelöscht, und auch die zugehörigen
          Dateien werden entfernt. Es bleiben keine Reste zurück, die sich wiederherstellen ließen.
        </p>

        {result && (
          <div className="alert alert--ok" role="status">
            Ihr Konto ist zurückgesetzt. Es wurden {result.rows.toLocaleString('de-DE')} Einträge und {result.files.toLocaleString('de-DE')} Dateien gelöscht.
          </div>
        )}

        <h3>Was gelöscht wird</h3>
        <ul className="small">
          <li>Kontakte, Anfragen, Angebote, Aufträge, Produkte, Termine, Aufgaben und alle anderen Datensätze</li>
          <li>Ausgestellte Rechnungen, Buchungen, Belege und das Auftragsbuch</li>
          <li>Mitarbeiter, Dokumente und Dateien aller Module, die Protokolle und alle API-Schlüssel und Verbindungen zu Diensten</li>
          <li>Alle anderen Benutzer und Rollen. Ihr eigener Zugang bleibt bestehen.</li>
        </ul>
        <h3>Was bleibt</h3>
        <ul className="small">
          <li>Ihr Paket, Ihre gebuchten Zusätze und Ihre Rechnungen von Wirkia</li>
          <li>Ihr Zugang als Inhaber mit der aktuellen Sitzung</li>
        </ul>

        <div className="alert alert--warn" role="note">
          Rechnungen, Buchungsbelege und das Auftragsbuch unterliegen gesetzlichen Aufbewahrungsfristen (zum Beispiel acht Jahre für Buchungsbelege nach der Abgabenordnung). Mit der
          Löschung übernehmen Sie die Verantwortung dafür. Laden Sie vorher über „Daten exportieren“ eine Sicherung herunter, wenn Sie die Unterlagen noch brauchen.
        </div>

        <p className="small">
          Möchten Sie nur einzelne Einträge ausblenden, ohne sie zu löschen, nutzen Sie dort die Funktion „Archivieren“. Das Löschen eines einzelnen Eintrags, zum Beispiel eines Debitors oder
          Kreditors, entfernt ihn dagegen ebenfalls endgültig aus der Datenbank. Das geht nur, solange keine Rechnung oder Buchung auf ihn verweist. Sonst bietet Wirkia das Archivieren an.
        </p>
      </section>

      {isOwner ? (
        <section className="card">
          <h2>Konto zurücksetzen</h2>
          <form onSubmit={submit} className="form" noValidate>
            {error && !passwordError && <div className="alert alert--error" role="alert">{error.message}</div>}
            <label className="field">
              <span>Ihr Passwort</span>
              <input className="input" type="password" autoComplete="current-password" value={password} disabled={reset.isPending} onChange={(e) => setPassword(e.target.value)} aria-invalid={!!passwordError} />
              {passwordError && <span className="field__error" role="alert">{passwordError}</span>}
            </label>
            <label className="field">
              <span>Zur Bestätigung tippen Sie „{ACCOUNT_RESET_PHRASE}“ ein</span>
              <input className="input" value={phrase} autoComplete="off" disabled={reset.isPending} onChange={(e) => setPhrase(e.target.value)} />
            </label>
            <div className="form-actions">
              <button className="btn btn--danger" type="submit" disabled={!ready}>
                {reset.isPending ? 'Wird gelöscht …' : 'Alle Daten endgültig löschen'}
              </button>
            </div>
            <p className="muted small">Das Löschen lässt sich nicht rückgängig machen. Je nach Datenmenge dauert es einen Moment.</p>
          </form>
        </section>
      ) : (
        <section className="card">
          <p className="muted">Nur der Inhaber kann das Konto zurücksetzen.</p>
        </section>
      )}
    </>
  );
}
